import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import { ReactComponent as DefaultAvatarIcon } from '@assets/icons/V4/default-profile-pic.svg';
import Button from '@components/V4/Button';
import Table from '@components/V4/Table/Table';
import { PastStudentDTO } from '@shared/clientTypes/DTOs/EnrollmentsDTO';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment from 'moment';
import { Link, useHistory } from 'react-router-dom';

interface PastStudentsTableProps {
  students?: PastStudentDTO[];
  isLoading: boolean;
}

const formatMentoredPeriod = (student: PastStudentDTO) => {
  const from = student.firstMentoredOn ? moment(student.firstMentoredOn).format('MMM YYYY') : null;
  const to = student.lastMentoredOn ? moment(student.lastMentoredOn).format('MMM YYYY') : null;

  if (from && to && from !== to) return `${from} – ${to}`;

  return from ?? to ?? 'n/a';
};

const StudentIdentity = (props: { student: PastStudentDTO }) => {
  const { student } = props;

  return (
    <Link
      to={`/student-profile/${student._id}`}
      className={mergeClassNames('flex items-center gap-4 text-white underline hover:underline', {
        'line-through': !!student.isDeactivated,
      })}
    >
      {student.avatar ? (
        <img src={student.avatar} crossOrigin="anonymous" alt="" className="h-8 w-8 rounded-full" />
      ) : (
        <DefaultAvatarIcon className="h-8 w-8 rounded-full" />
      )}
      <span>{student.fullName}</span>
    </Link>
  );
};

const StudentStatus = (props: { isDeactivated?: boolean }) => (
  <p
    className={mergeClassNames({
      'text-customRed': !!props.isDeactivated,
      'text-customLime': !props.isDeactivated,
    })}
  >
    {props.isDeactivated ? 'Inactive' : 'Active'}
  </p>
);

const PastStudentsTable = (props: PastStudentsTableProps) => {
  const { students, isLoading } = props;
  const history = useHistory();

  const viewProfile = (studentId: string) => history.push(`/student-profile/${studentId}`);

  return (
    <>
      <Table isLoading={isLoading} isUserView className="hidden w-full tablet:table">
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell>Student</Table.HeaderCell>
            <Table.HeaderCell>Courses</Table.HeaderCell>
            <Table.HeaderCell>Enrollments</Table.HeaderCell>
            <Table.HeaderCell>Mentored</Table.HeaderCell>
            <Table.HeaderCell>Graduation</Table.HeaderCell>
            <Table.HeaderCell>Student Status</Table.HeaderCell>
            <Table.HeaderCell> </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {!isLoading && !students?.length && (
            <tr>
              <td colSpan={7} className="h-64">
                <EmptyStateCard
                  icon={<BlockIcon className="text-customGrey" />}
                  text="You have no past students yet."
                />
              </td>
            </tr>
          )}
          {students?.map((student) => (
            <Table.Row key={student._id} isUserView>
              <Table.Cell className="rounded-l-2xl">
                <StudentIdentity student={student} />
              </Table.Cell>
              <Table.Cell>{student.courses.join(', ') || 'n/a'}</Table.Cell>
              <Table.Cell>{student.enrollmentsCount}</Table.Cell>
              <Table.Cell>{formatMentoredPeriod(student)}</Table.Cell>
              <Table.Cell>{student.graduationYear ?? 'n/a'}</Table.Cell>
              <Table.Cell>
                <StudentStatus isDeactivated={student.isDeactivated} />
              </Table.Cell>
              <Table.Cell className="rounded-r-2xl">
                <Button buttonText="View profile" onClick={() => viewProfile(student._id)} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <div className="flex flex-col gap-4 tablet:hidden">
        {!isLoading && !students?.length && (
          <EmptyStateCard
            icon={<BlockIcon className="text-customGrey" />}
            text="You have no past students yet."
          />
        )}
        {students?.map((student) => (
          <div key={student._id} className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-4">
            <StudentIdentity student={student} />
            <div className="flex flex-col gap-1 font-raleway text-xs text-customGrey">
              <span>Courses: {student.courses.join(', ') || 'n/a'}</span>
              <span>Mentored: {formatMentoredPeriod(student)}</span>
              <span className="flex gap-1">
                Status: <StudentStatus isDeactivated={student.isDeactivated} />
              </span>
            </div>
            <Button buttonText="View profile" onClick={() => viewProfile(student._id)} />
          </div>
        ))}
      </div>
    </>
  );
};

export default PastStudentsTable;
